import React, { useEffect, useMemo, useState } from "react";
import ToolbarHeader from "../../components/ToolbarHeader";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import CustomAccordion from "../../components/CustomAccordion";
import CustomTable from "../../components/CustomTable";
import { useAuth } from "../../contexts/AuthContext";
import {
  adminColumns,
  areaManagerColumns,
} from "../../components/Columns/VoucherDetails";
import PreviewModal from "./PreviewModal";
import useApi from "../../hooks/useApi";
import { apiUrls } from "../../utils/api/apiUrls";
import { toast } from "react-toastify";
import CustomButton from "../../components/CustomButton";
import RejectModal from "../../CustomRejectModal/RejectModal";
import EditApproveModal from "../../CustomEditApproveModal/EditApproveModal";
import {
  approvedAmountPassAccordingToLevel,
  areaManagerLineApprovedAmountDisplay,
  canSendBackForRework,
  downloadReports,
  hasAnyChildInRework,
  mergeL3FinanceFromWorkflowStep,
  voucherRejectedAmountForViewer,
} from "../../utils/helpers";
import PreviewDocModal from "../cashDeposits/PreviewDocModal";
import EditVoucherComment from "../../CustomEditVoucherCommentModal/EditVoucherComment";
import SendBackForReworkModal from "../../CustomSendBackForReworkModal/SendBackForReworkModal";
import EditAfterReworkModal from "../../CustomEditAfterReworkModal/EditAfterReworkModal";
import {
  collectVoucherDetailLineItems,
  mapChildDocumentRow,
} from "../../utils/dmsStandaloneListUtils";

/** First matching attribute (API may use voucher_head vs head, etc.). */
const getAttr = (doc, ...names) => {
  if (!doc?.dataModalAttributeSet?.length || !names.length) return undefined;
  for (const name of names) {
    const hit = doc.dataModalAttributeSet.find(
      (attr) => attr?.metadataTechnicalName === name
    );
    const v = hit?.attributeValue;
    if (v != null && String(v).trim() !== "") return v;
  }
  return undefined;
};

const sanitizeActionComment = (comment) => {
  if (!comment) return "";
  const trimmed = String(comment).trim();
  if (!trimmed) return "";
  const lower = trimmed.toLowerCase();
  if (lower === "bulk send all - approved") return "Approved";
  if (lower === "bulk send all - rejected") return "Rejected";
  if (lower === "resubmitted after rework") return "";
  return comment;
};

/**
 * Store list rows may be a thin document slice; detail needs getRecordById (getVoucherDetails) payload.
 * @param {{ reworkListNavigation?: boolean }} [opts] - Rework tab list rows may include `documents[]`
 *   from getAllRecords that are not the full voucher shape — require bucket arrays instead.
 */
function storeVoucherStateHasRenderableDocs(s, opts = {}) {
  const { reworkListNavigation = false } = opts;
  if (!s || typeof s !== "object") return false;
  const bucketKeys = [
    "pendingDocs",
    "approvedDocs",
    "rejectedDocs",
    "pendingApprovedRejectedDocs",
    "reworkRequestedDocs",
    "reworkPendingDocs",
  ];
  if (bucketKeys.some((k) => Array.isArray(s[k]) && s[k].length > 0)) {
    return true;
  }
  // List navigation often passes `documents[]` placeholders without `dataModalAttributeSet`.
  // Treat as "has docs" only when at least one row can render (otherwise fetch getRecordById).
  if (
    !reworkListNavigation &&
    Array.isArray(s.documents) &&
    s.documents.length > 0 &&
    s.documents.some(
      (d) => Array.isArray(d?.dataModalAttributeSet) && d.dataModalAttributeSet.length > 0
    )
  ) {
    return true;
  }
  return false;
}

const convertDataStructure = (inputData) => {
  let convertedDat = [];
  inputData?.forEach((doc) => {
    const l3Merged = mergeL3FinanceFromWorkflowStep(doc);
    convertedDat.push({
      head: getAttr(doc, "head", "voucher_head"),
      id: doc?.id,
      filename: doc?.documentName,
      date: getAttr(doc, "date", "voucher_date", "submitted_on"),
      amount:
        getAttr(doc, "amount", "voucher_amount", "requested_amount") ?? doc?.amount,
      comment: getAttr(doc, "comment", "voucher_comment"),
      childId: doc.childId,
      l1_amount_approved:
        getAttr(doc, "l1_amount_approved") ?? doc?.l1_amount_approved,
      l2_amount_approved:
        getAttr(doc, "l2_amount_approved") ?? doc?.l2_amount_approved,
      finance_amount_approved:
        getAttr(doc, "finance_amount_approved") ?? doc?.finance_amount_approved,
      L1: {
        status:
          doc?.l1?.status ??
          doc?.L1?.status ??
          doc?.workflowStepResponses?.find((step) => step.userLabel === "L1")
            ?.status,
      },
      L2: {
        status:
          doc?.l2?.status ??
          doc?.L2?.status ??
          doc?.workflowStepResponses?.find((step) => step.userLabel === "L2")
            ?.status,
      },
      L3: {
        status: l3Merged?.status,
        approvedAmount: l3Merged?.approvedAmount,
      },
      newL1Status: doc?.l1?.status,
      newL2Status: doc?.l2?.status,
      newL3Status: doc?.l3?.status ?? l3Merged?.status,
      newL1ActionComment: sanitizeActionComment(doc?.l1?.actionComment),
      newL2ActionComment: sanitizeActionComment(doc?.l2?.actionComment),
      newL3ActionComment: sanitizeActionComment(doc?.l3?.actionComment),
      store_code: doc?.storeCode,
      store_name: doc?.storeName,
      reworkStatus: doc?.reworkStatus,
      reworkRequestedByLevel: doc?.reworkRequestedByLevel,
      reworkRequestedAt: doc?.reworkRequestedAt,
      reworkComment: doc?.reworkComment,
      // Preserve workflowStepResponses and level-specific data for decision checking
      workflowStepResponses: doc?.workflowStepResponses,
      l1: doc?.l1,
      l2: doc?.l2,
      l3: doc?.l3 ?? l3Merged,
      Finance: doc?.Finance ?? doc?.L3 ?? l3Merged,
    });
  });
  return convertedDat;
};

const VoucherDetails = () => {
  const { vid } = useParams();
  const { state: fromPrevScreen } = useLocation();
  const navigate = useNavigate();

  // Fallback to sessionStorage if state is not available
  const getInitialState = () => {
    if (fromPrevScreen) return fromPrevScreen;
    try {
      const stored = sessionStorage.getItem(`voucher_${vid}_state`);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error("Error reading from sessionStorage:", e);
    }
    return null;
  };

  const [state, setDataState] = useState(getInitialState());

  // Keep local state in sync when opening another voucher on the same route (vid / location.state change).
  useEffect(() => {
    if (fromPrevScreen != null) {
      setDataState(fromPrevScreen);
      return;
    }
    try {
      const stored = sessionStorage.getItem(`voucher_${vid}_state`);
      if (stored) {
        setDataState(JSON.parse(stored));
        return;
      }
    } catch (_) {
      /* ignore */
    }
    setDataState(null);
  }, [vid, fromPrevScreen]);

  const {
    user: { userLabel } = {},
    setDownloadPreviewOpenWithData,
    downloadPreviewOpenWithData,
    currentStoreSelect,
    storeDropdownState,
  } = useAuth();

  const { apiCall: fileDownload } = useApi();
  const { apiCall: voucherStatusApi } = useApi();
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isRejectVisible, setIsRejectVisible] = useState({
    isOpen: false,
    data: {},
  });
  const [isEditApproveVisible, setIsEditApproveVisible] = useState({
    isOpen: false,
    data: {},
  });
  const [isEditVoucherCommentVisible, setIsEditVoucherCommentVisible] =
    useState({
      isOpen: false,
      data: {},
    });
  const [isSendBackForReworkVisible, setIsSendBackForReworkVisible] = useState({
    isOpen: false,
    data: [],
  });
  const [isEditAfterReworkVisible, setIsEditAfterReworkVisible] = useState({
    isOpen: false,
    data: {},
  });
  const [selectedCheckBoxes, setSelectedCheckBoxes] = useState([]);
  const [decisionStatusMap, setDecisionStatusMap] = useState({}); // Track decision status per sub-voucher
  const [canSendAll, setCanSendAll] = useState(false); // Track if all sub-vouchers have decisions

  // Choose the best available docs list for the current view.
  // For Pending: use pendingApprovedRejectedDocs when available so we show all sub-vouchers (pending + approved at this level)
  // and Submit enables when all have decisions; otherwise pendingDocs only would hide approved rows.
  const docsForView = useMemo(() => {
    if (!state) return [];
    // Approved view: show only L1-approved/partially-approved items (like Rejected tab shows only rejected)
    if (state?.type === "Approved") {
      const approved = state?.approvedDocs;
      const voucherId = state?.id != null ? Number(state.id) : null;
      // Approved tab used to pass `approvedDocs: [parentRow]`, which is not a line-item list.
      const isParentRowMasqueradingAsDoc =
        Array.isArray(approved) &&
        approved.length === 1 &&
        voucherId != null &&
        approved[0]?.id != null &&
        Number(approved[0].id) === voucherId;
      if (approved?.length && !isParentRowMasqueradingAsDoc) return approved;
      const allDocs = state?.pendingApprovedRejectedDocs || state?.pendingDocs || [];
      return allDocs.filter((doc) => {
        const status = doc?.l1?.status ?? doc?.newL1Status ?? doc?.workflowStepResponses?.find((s) => s.userLabel !== "Store")?.status;
        const s = (status || "").toLowerCase();
        return s === "approved" || s === "partially approved" || s === "partiallyapproved" || s === "rework approved" || s === "reworkapproved";
      });
    }
    if (state?.type === "Pending") return state?.pendingApprovedRejectedDocs?.length ? state.pendingApprovedRejectedDocs : (state?.pendingDocs || []);
    if (state?.type === "Rework")
      // Prefer combined/approved docs first to include rework-approved rows
      return (
        state?.pendingApprovedRejectedDocs ||
        state?.approvedDocs ||
        state?.pendingDocs ||
        state?.rejectedDocs ||
        []
      );
    // View All from list: navigation state has documents[] but often no pendingApprovedRejectedDocs yet.
    if (state?.type === "All") {
      return state?.pendingApprovedRejectedDocs?.length
        ? state.pendingApprovedRejectedDocs
        : state?.documents || [];
    }
    return state?.rejectedDocs || [];
  }, [state]);

  const outputData = useMemo(
    () => (userLabel === "Store" ? state : convertDataStructure(docsForView)),
    [state, userLabel, docsForView]
  );

  // For Store: API (getRecordById) returns pendingDocs, approvedDocs, etc., not "documents".
  // After edit/refresh we set state from API, so state.documents is missing and the table would be empty.
  // Derive combined document list for Store table when documents is not present.
  const storeTableData = useMemo(() => {
    if (userLabel !== "Store" || !outputData) return [];
    const lineItems = collectVoucherDetailLineItems(outputData);
    if (!lineItems.length) return [];
    return lineItems.map((doc) => {
      if (doc?.dataModalAttributeSet?.length) {
        return convertDataStructure([doc])[0];
      }
      return mapChildDocumentRow(doc);
    });
  }, [userLabel, outputData]);

  // Calculate total approved amount from sub-vouchers
  const calculateTotalApprovedAmount = useMemo(() => {
    if (!outputData || !Array.isArray(outputData)) return null;
    
    // Filter out parent rows (rows with subRows) - we only care about actual sub-vouchers
    const actualSubVouchers = outputData.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    
    if (actualSubVouchers.length === 0) return null;

    if (userLabel === "L1") {
      const totalL1 = actualSubVouchers.reduce((sum, sub) => {
        const raw = sub?.l1_amount_approved ?? sub?.L1?.approvedAmount;
        if (raw == null || raw === "") return sum;
        const n = Number(raw);
        return sum + (Number.isFinite(n) ? n : 0);
      }, 0);
      return totalL1 > 0 ? totalL1 : null;
    }

    // Must match per-line "Approved Amount" in areaManagerColumns (areaManagerLineApprovedAmountDisplay), including
    // nested L2/L3/Finance when flat l2_amount_approved / finance_amount_approved are missing.
    const totalApproved = actualSubVouchers.reduce((sum, sub) => {
      const n = areaManagerLineApprovedAmountDisplay(sub, userLabel);
      if (n == null || n === "") return sum;
      const num = Number(n);
      if (!Number.isFinite(num) || num <= 0) return sum;
      return sum + num;
    }, 0);

    return totalApproved > 0 ? totalApproved : null;
  }, [outputData, userLabel]);

  const onClickDownloadFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        const url = window.URL.createObjectURL(new Blob([apiData.data]));
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      },
    });
  };

  const voucherStatusApiCall = (payload, status, saveDecisionOnly = false) => {
    voucherStatusApi({
      url: apiUrls.dmsService.approveRequest,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        setIsRejectVisible({ isOpen: false });
        setIsEditApproveVisible({ isOpen: false });
        setIsSendBackForReworkVisible({ isOpen: false });
        toast.success(status);
        
        // Update decision status map for saved decisions
        // Store optimistic updates to merge with API response
        if (saveDecisionOnly) {
          const optimisticDecisionMap = { ...decisionStatusMap };
          if (payload.approved && payload.approved.length > 0) {
            payload.approved.forEach((item) => {
              optimisticDecisionMap[item.id] = "Approved";
            });
          }
          if (payload.rejected && payload.rejected.length > 0) {
            payload.rejected.forEach((id) => {
              optimisticDecisionMap[id] = "Rejected";
            });
          }
          setDecisionStatusMap(optimisticDecisionMap);
          checkIfAllDecisionsMade(optimisticDecisionMap);
        }
        
        // Refresh data - updateDecisionStatusFromData will merge with optimistic updates
        updateVoucherData();
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || "Operation failed";
        toast.error(errorMessage);
        
        // If error mentions pending sub-vouchers, highlight them
        if (errorMessage.includes("Please complete all sub-voucher actions")) {
          toast.warning("Please complete all sub-voucher decisions before proceeding");
        }
      },
    });
  };

  const updateCommentApiCall = (payload, status) => {
    voucherStatusApi({
      url: apiUrls.dmsService.updateVoucherComment,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        setIsEditVoucherCommentVisible({ isOpen: false });
        toast.success(status);
        updateVoucherData();
      },
    });
  };

  const updateVoucherData = (onComplete, skipNavigation = false) => {
    // When vouchers transition to/from rework, we need to refresh the canSendAll state
    // This will be called after updateVoucherData completes via useEffect
    voucherStatusApi({
      url: apiUrls.dmsService.getVoucherDetails + vid,
      method: "post",
      onSuccess: (apiData) => {
        const nextType = state?.type || apiData?.type || "Rework";
        setDataState({ ...apiData, type: nextType });
        
        // Only navigate if not skipping (for Store users or after edit, skip navigation to prevent reload)
        if (!skipNavigation) {
          navigate(".", {
            replace: true,
            state: { ...apiData, type: nextType },
          });
        } else {
          // For Store users, update sessionStorage to preserve state
          try {
            sessionStorage.setItem(`voucher_${vid}_state`, JSON.stringify({ ...apiData, type: nextType }));
          } catch (e) {
            console.error("Error saving to sessionStorage:", e);
          }
        }
        
        // Don't call updateDecisionStatusFromData here - useEffect will handle it
        // This prevents double updates and race conditions
        
        // Call completion callback if provided
        if (onComplete) {
          onComplete();
        }
      },
      onError: (error) => {
        console.error("Error fetching voucher details:", error);
        const errorMessage = error?.response?.data?.message || "Failed to fetch voucher details";
        toast.error(errorMessage);
        
        // Call completion callback even on error
        if (onComplete) {
          onComplete();
        }
      },
    });
  };

  // Update decision status map from API data
  // Merges API data with optimistic updates to prevent race conditions
  const updateDecisionStatusFromData = (apiData) => {
    try {
      // Preserve optimistic updates that might not be in API response yet
      const mergedDecisionMap = { ...decisionStatusMap };
      const allDocs = [
        ...(apiData?.pendingDocs || []),
        ...(apiData?.approvedDocs || []),
        ...(apiData?.rejectedDocs || []),
        ...(apiData?.pendingApprovedRejectedDocs || []),
      ];
      
      if (!Array.isArray(allDocs)) {
        console.warn("updateDecisionStatusFromData: allDocs is not an array", allDocs);
        return;
      }
      
      allDocs.forEach((doc) => {
        if (!doc || !doc.id) {
          return; // Skip invalid docs
        }
        
        // Check if sub-voucher has a decision made by checking the current step's action
        // When saveDecisionOnly=true, backend sets currentStepInstance.action but doesn't advance workflow
        // So we need to check the action on the current step for the acting user level
        
        // First, try to find the current step for this user level in workflowStepResponses
        // The current step should have an action set when saveDecisionOnly=true
        let currentStep = null;
        
        if (doc?.workflowStepResponses && Array.isArray(doc.workflowStepResponses)) {
          // Find step matching user level - prioritize steps with actions set (saved decisions)
          // NOTE: Backend maps action to 'status' field in WorkflowStepResponse
          currentStep = doc.workflowStepResponses.find(
            (step) => step?.userLabel === userLabel && 
                     (step.status === "Approved" || 
                      step.status === "Rejected" || 
                      step.status === "ReworkApproved" || 
                      step.status === "Partially Approved" ||
                      // Also check action field for backward compatibility
                      step.action === "Approved" || 
                      step.action === "Rejected" || 
                      step.action === "ReworkApproved" || 
                      step.action === "Partially Approved")
          );
          
          // If no step with action found, find any step for this user level (might be pending)
          if (!currentStep) {
            currentStep = doc.workflowStepResponses.find(
              (step) => step?.userLabel === userLabel
            );
          }
        }
        
        // Check current step's action/status (this is set when saveDecisionOnly=true)
        // Backend maps action to 'status' field, so check status first, then action for backward compatibility
        const stepAction = currentStep?.status || currentStep?.action;
        const stepActionLower = stepAction?.toLowerCase();
        
        // CRITICAL: Always check status fields because "Partially Approved" is computed on doc.getL2().status
        // but workflowStepResponse.status stays "Approved", so we MUST check status fields to catch it
        let status = null;
        if (userLabel === "L1") {
          status = doc?.l1?.status || doc?.newL1Status;
        } else if (userLabel === "L2") {
          status = doc?.l2?.status || doc?.newL2Status;
        } else if (userLabel === "Finance") {
          status = doc?.l3?.status || doc?.newL3Status || doc?.Finance?.status;
        } else {
          status = doc?.l1?.status || doc?.l2?.status || doc?.l3?.status || doc?.newL1Status || doc?.newL2Status || doc?.newL3Status;
        }
        
        // Check status fields first (this catches "Partially Approved" computed by backend)
        if (status) {
          const statusLower = status.toLowerCase();
          if (["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(statusLower)) {
            mergedDecisionMap[doc.id] = "Approved";
          } else if (statusLower === "rejected") {
            mergedDecisionMap[doc.id] = "Rejected";
          }
        }
        
        // Fallback: Check step action if status fields didn't have a decision
        // This handles cases where status fields might not be populated yet
        if (!mergedDecisionMap[doc.id]) {
          if (stepActionLower && ["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(stepActionLower)) {
            mergedDecisionMap[doc.id] = "Approved";
          } else if (stepActionLower === "rejected") {
            mergedDecisionMap[doc.id] = "Rejected";
          }
        }
      });
      
      setDecisionStatusMap(mergedDecisionMap);
      checkIfAllDecisionsMade(mergedDecisionMap);
    } catch (error) {
      console.error("Error in updateDecisionStatusFromData:", error);
      // Don't break the app - just log the error
    }
  };

  // Helper function to check if a voucher is in rework state
  // This prevents bulk send when vouchers are going backward (rework)
  const checkIfVoucherInRework = (sub) => {
    // CRITICAL: Check status fields FIRST - if status is "Rework Approved", rework is COMPLETE
    // Vouchers with "Rework Approved" status are NOT in rework anymore
    const statusFields = [
      sub?.status,
      sub?.L1?.status,
      sub?.L2?.status,
      sub?.L3?.status,
      sub?.Finance?.status,
      sub?.newL1Status,
      sub?.newL2Status,
      sub?.newL3Status
    ];
    
    for (const status of statusFields) {
      if (status) {
        const statusLower = status.toLowerCase();
        // If status is "Rework Approved" or "ReworkApproved", rework is COMPLETE - voucher is NOT in rework
        if (statusLower === "rework approved" || statusLower === "reworkapproved") {
          return false; // Rework is complete, voucher is NOT in rework
        }
        // Check for active rework states
        if (
          status.includes("Rework Requested") ||
          status.includes("Rework Pending") ||
          status.includes("ReworkRequested") ||
          status.includes("ReworkPending")
        ) {
          return true;
        }
      }
    }

    // Check approval_status metadata field
    const approvalStatusAttr = sub?.dataModalAttributeSet?.find(
      (attr) => attr?.metadataTechnicalName === "approval_status"
    );
    const approvalStatus = approvalStatusAttr?.attributeValue;
    
    if (approvalStatus && (
      approvalStatus.includes("ReworkRequested") || 
      approvalStatus.includes("ReworkPending")
    )) {
      return true;
    }

    // Check workflow step responses for rework status
    // IMPORTANT: Check the LATEST/MOST RECENT step, not all steps
    // If latest step is "ReworkApproved", rework is complete
    if (sub?.workflowStepResponses && Array.isArray(sub.workflowStepResponses)) {
      // Get the most recent step (last in array, or step with latest actionDate)
      const steps = [...sub.workflowStepResponses];
      const latestStep = steps.length > 0 ? steps[steps.length - 1] : null;
      
      // If latest step is ReworkApproved, rework is complete
      if (latestStep) {
        const latestStatus = latestStep?.status || latestStep?.action;
        if (latestStatus && (
          latestStatus === "ReworkApproved" || 
          latestStatus.toLowerCase() === "reworkapproved" ||
          latestStatus === "Rework Approved"
        )) {
          return false; // Rework is complete
        }
      }
      
      // Check all steps for active rework states
      for (const step of sub.workflowStepResponses) {
        const stepStatus = step?.status;
        if (stepStatus && (
          stepStatus.includes("ReworkRequested") ||
          stepStatus.includes("ReworkPending") ||
          stepStatus === "L1ReworkRequested" ||
          stepStatus === "L1ReworkPending" ||
          stepStatus === "L2ReworkRequested" ||
          stepStatus === "L2ReworkPending" ||
          stepStatus === "AccountsReworkRequested" ||
          stepStatus === "AccountsReworkPending"
        )) {
          return true;
        }
        
        // Check action field for rework indicators (but exclude ReworkApproved)
        const action = step?.action;
        if (action && action !== "ReworkApproved" && (
          action.toUpperCase().includes("REWORK") ||
          action === "REWORK_STARTED" ||
          action === "REWORK_REQUESTED"
        )) {
          return true;
        }
      }
    }

    return false;
  };

  // Helper to extract selected vouchers from checkboxes
  const getSelectedVouchers = () => {
    const selectedVouchers = [];
    if (!selectedCheckBoxes || selectedCheckBoxes.length === 0) {
      return selectedVouchers;
    }
    
    selectedCheckBoxes.forEach((row) => {
      if (row?.original?.subRows) {
        row?.original?.subRows?.forEach((subRow) => {
          if (subRow?.id) {
            selectedVouchers.push(subRow);
          }
          subRow?.subRows?.forEach((nestedRow) => {
            if (nestedRow?.id) {
              selectedVouchers.push(nestedRow);
            }
          });
        });
      } else if (row?.original?.id) {
        selectedVouchers.push(row.original);
      }
    });
    
    return selectedVouchers;
  };

  // Helper to get vouchers to process based on user level
  // L1 users: use selected vouchers from checkboxes
  // L2/Finance users: use all vouchers from outputData
  const getVouchersToProcess = () => {
    if (userLabel === "L1") {
      return getSelectedVouchers();
    } else {
      // For L2/Finance: get all vouchers from outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      return allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    }
  };

  // Helper to check if a voucher is already approved at user's level
  const isVoucherApproved = (voucher) => {
    // Check decision status map first
    const decision = decisionStatusMap[voucher?.id];
    if (decision === "Approved") {
      return true;
    }
    
    // Check workflow step responses
    if (voucher?.workflowStepResponses && Array.isArray(voucher.workflowStepResponses)) {
      const userStep = voucher.workflowStepResponses.find(
        (step) => step.userLabel === userLabel
      );
      // Check both status and action fields (backend maps action to status)
      const stepAction = userStep?.status || userStep?.action;
      if (stepAction && ["Approved", "ReworkApproved", "Partially Approved"].includes(stepAction)) {
        return true;
      }
    }
    
    // Check status fields based on user level
    let status = null;
    if (userLabel === "L1") {
      status = voucher?.newL1Status || voucher?.L1?.status;
    } else if (userLabel === "L2") {
      status = voucher?.newL2Status || voucher?.L2?.status;
    } else if (userLabel === "Finance") {
      status = voucher?.newL3Status || voucher?.L3?.status || voucher?.Finance?.status;
    }
    
    if (status && ["Approved", "ReworkApproved", "Partially Approved"].includes(status)) {
      return true;
    }
    
    return false;
  };

  // Helper to check if a voucher is already rejected at user's level
  const isVoucherRejected = (voucher) => {
    // Check decision status map first
    const decision = decisionStatusMap[voucher?.id];
    if (decision === "Rejected") {
      return true;
    }
    
    // Check workflow step responses
    if (voucher?.workflowStepResponses && Array.isArray(voucher.workflowStepResponses)) {
      const userStep = voucher.workflowStepResponses.find(
        (step) => step.userLabel === userLabel
      );
      // Check both status and action fields (backend maps action to status)
      const stepAction = userStep?.status || userStep?.action;
      if (stepAction === "Rejected") {
        return true;
      }
    }
    
    // Check status fields based on user level
    let status = null;
    if (userLabel === "L1") {
      status = voucher?.newL1Status || voucher?.L1?.status;
    } else if (userLabel === "L2") {
      status = voucher?.newL2Status || voucher?.L2?.status;
    } else if (userLabel === "Finance") {
      status = voucher?.newL3Status || voucher?.L3?.status || voucher?.Finance?.status;
    }
    
    if (status === "Rejected") {
      return true;
    }
    
    return false;
  };

  // Helper to check if selected vouchers can be sent back for rework
  // Must be defined after checkIfVoucherInRework
  const canSendSelectedBackForRework = () => {
    // For L1 users: check selected vouchers (they have checkboxes)
    // For L2/Finance users: check all vouchers in outputData (no checkboxes)
    let vouchersToCheck = [];
    
    if (userLabel === "L1") {
      vouchersToCheck = getSelectedVouchers();
      if (vouchersToCheck.length === 0) {
        return false;
      }
    } else {
      // For L2/Finance: check all vouchers in outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      vouchersToCheck = allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
      if (vouchersToCheck.length === 0) {
        return false;
      }
    }
    
    // Return true if at least one voucher can be sent back for rework
    return vouchersToCheck.some((voucher) => {
      // If voucher is already in rework, cannot send it back again
      if (checkIfVoucherInRework(voucher)) {
        return false;
      }
      
      // Check if voucher can be sent back for rework at user's level
      // Voucher must be pending at user's level and not already approved/rejected
      return canSendBackForRework(voucher, userLabel);
    });
  };

  // Helper to check if selected vouchers can be approved
  const canApproveSelectedVouchers = () => {
    // For L1 users: check selected vouchers (they have checkboxes)
    // For L2/Finance users: check all vouchers in outputData (no checkboxes)
    let vouchersToCheck = [];
    
    if (userLabel === "L1") {
      vouchersToCheck = getSelectedVouchers();
      if (vouchersToCheck.length === 0) {
        return false;
      }
    } else {
      // For L2/Finance: check all vouchers in outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      vouchersToCheck = allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
      if (vouchersToCheck.length === 0) {
        return false;
      }
    }
    
    // Return true if at least one voucher can be approved
    // (i.e., not already approved, not rejected, and not in rework)
    return vouchersToCheck.some((voucher) => {
      return !isVoucherApproved(voucher) && 
             !isVoucherRejected(voucher) && 
             !checkIfVoucherInRework(voucher);
    });
  };

  // Helper to check if selected vouchers can be rejected
  const canRejectSelectedVouchers = () => {
    // For L1 users: check selected vouchers (they have checkboxes)
    // For L2/Finance users: check all vouchers in outputData (no checkboxes)
    let vouchersToCheck = [];
    
    if (userLabel === "L1") {
      vouchersToCheck = getSelectedVouchers();
      if (vouchersToCheck.length === 0) {
        return false;
      }
    } else {
      // For L2/Finance: check all vouchers in outputData
      const allVouchers = Array.isArray(outputData) ? outputData : [];
      vouchersToCheck = allVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
      if (vouchersToCheck.length === 0) {
        return false;
      }
    }
    
    // Return true if at least one voucher can be rejected
    // (i.e., not already rejected, not approved, and not in rework)
    return vouchersToCheck.some((voucher) => {
      return !isVoucherRejected(voucher) && 
             !isVoucherApproved(voucher) && 
             !checkIfVoucherInRework(voucher);
    });
  };

  // Check if all sub-vouchers have decisions made
  const checkIfAllDecisionsMade = (decisionMap = decisionStatusMap) => {
    // Use the rendered rows (outputData) for decision tracking to stay in sync with the table
    const subVouchers = Array.isArray(outputData) ? outputData : [];
    if (!subVouchers.length) {
      setCanSendAll(false);
      return;
    }

    // Filter out parent rows (rows with subRows) - we only care about actual sub-vouchers
    const actualSubVouchers = subVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    
    if (actualSubVouchers.length === 0) {
      setCanSendAll(false);
      return;
    }

    const allHaveDecisions = actualSubVouchers.every((sub) => {
      // If sub-voucher has a decision at current level (Approved/Rejected/Partially Approved), it counts as "has decision"
      // even if it was previously in rework (e.g. resubmitted after rework then rejected)
      const decision = decisionMap[sub.id];
      const statusForLevel = userLabel === "L1" ? (sub?.newL1Status || sub?.L1?.status || sub?.l1?.status)
        : userLabel === "L2" ? (sub?.newL2Status || sub?.L2?.status || sub?.l2?.status)
        : (sub?.newL3Status || sub?.L3?.status || sub?.l3?.status || sub?.Finance?.status);
      const statusLower = (statusForLevel || "").toLowerCase().trim();
      const hasDecisionAtLevel = decision === "Approved" || decision === "Rejected"
        || ["approved", "reworkapproved", "partially approved", "partiallyapproved"].includes(statusLower)
        || statusLower === "rejected" || statusLower === "stopped";

      if (hasDecisionAtLevel) return true;

      // CRITICAL: Vouchers in rework without a decision cannot be sent forward
      const isInRework = checkIfVoucherInRework(sub);
      if (isInRework) {
        return false; // Voucher in rework and no decision yet - cannot send forward
      }

      // CRITICAL: Check if voucher has moved past current user's level
      // If approval_status indicates voucher is at a higher level, current user has already made decisions
      const approvalStatusAttr = sub?.dataModalAttributeSet?.find(
        (attr) => attr?.metadataTechnicalName === "approval_status"
      );
      const approvalStatus = approvalStatusAttr?.attributeValue;
      
      if (approvalStatus) {
        const approvalStatusUpper = approvalStatus.toUpperCase();
        // If user is L1 and voucher is at L2 or Finance level, L1 has made decisions
        if (userLabel === "L1" && (
          approvalStatusUpper.includes("L2APPROVALPENDING") ||
          approvalStatusUpper.includes("ACCOUNTSAPPROVALPENDING") ||
          approvalStatusUpper.includes("L2REJECTED") ||
          approvalStatusUpper.includes("ACCOUNTSREJECTED")
        )) {
          return true; // Voucher has moved past L1, so L1 has made decisions
        }
        // If user is L2 and voucher is at Finance level, L2 has made decisions
        if (userLabel === "L2" && (
          approvalStatusUpper.includes("ACCOUNTSAPPROVALPENDING") ||
          approvalStatusUpper.includes("ACCOUNTSREJECTED")
        )) {
          return true; // Voucher has moved past L2, so L2 has made decisions
        }
        
        // CRITICAL: If voucher is still at a previous level (e.g., L1 approval/rework pending when user is L2),
        // L2 hasn't made a decision yet. But if voucher is L1REJECTED, journey is closed — treat as "no decision needed" so it doesn't block Submit All.
        if (userLabel === "L2" && approvalStatusUpper.includes("L1REJECTED")) {
          return true; // Rejected at L1, journey closed; don't block L2 Submit All
        }
        if (userLabel === "L2" && (
          approvalStatusUpper.includes("L1APPROVALPENDING") ||
          approvalStatusUpper.includes("L1REWORKREQUESTED") ||
          approvalStatusUpper.includes("L1REWORKPENDING")
        )) {
          return false; // Voucher is still at L1 level, L2 hasn't made a decision yet
        }
        // For Finance: L1/L2 rejected = journey closed; L2/L1 approval or rework pending = not at Finance yet.
        if (userLabel === "Finance" && (
          approvalStatusUpper.includes("L1REJECTED") ||
          approvalStatusUpper.includes("L2REJECTED")
        )) {
          return true; // Rejected at L1 or L2, journey closed; don't block L3 Submit All
        }
        if (userLabel === "Finance" && (
          approvalStatusUpper.includes("L1APPROVALPENDING") ||
          approvalStatusUpper.includes("L1REWORKREQUESTED") ||
          approvalStatusUpper.includes("L1REWORKPENDING") ||
          approvalStatusUpper.includes("L2APPROVALPENDING") ||
          approvalStatusUpper.includes("L2REWORKREQUESTED") ||
          approvalStatusUpper.includes("L2REWORKPENDING")
        )) {
          return false; // Voucher is still at L1 or L2 level, Finance hasn't made a decision yet
        }
      }

      // First check the decision map (populated from API data)
      if (decision === "Approved" || decision === "Rejected") return true;

      // Fallback: Check workflow step actions directly from the row data
      // This handles cases where decisionMap might not be updated yet
      let currentStep = null;
      
      // Check if row has workflowStepResponses (from convertDataStructure or API)
      // Backend uses STOPPED for rejected (journey closed); treat as final decision so Submit All enables for L3
      if (sub?.workflowStepResponses && Array.isArray(sub.workflowStepResponses)) {
        // Find step matching user level with an action set (saved decision)
        // NOTE: Backend maps action to 'status' field in WorkflowStepResponse
        // IMPORTANT: Check for ReworkApproved first as it indicates a completed decision
        currentStep = sub.workflowStepResponses.find(
          (step) => step.userLabel === userLabel && (
            step.status === "Approved" || 
            step.status === "Rejected" || 
            step.status === "STOPPED" ||
            step.status === "ReworkApproved" || 
            step.status === "Partially Approved" ||
            // Also check action field for backward compatibility
            step.action === "Approved" || 
            step.action === "Rejected" || 
            step.action === "STOPPED" ||
            step.action === "ReworkApproved" || 
            step.action === "Partially Approved"
          )
        );
        
        // If no step with action found, check if latest step for this user level indicates a decision
        // This handles cases where ReworkApproved is the latest step
        if (!currentStep) {
          const stepsForUser = sub.workflowStepResponses.filter(
            (step) => step.userLabel === userLabel
          );
          if (stepsForUser.length > 0) {
            // Get the latest step (last in array or by actionDate)
            const latestStep = stepsForUser[stepsForUser.length - 1];
            if (latestStep && (
              latestStep.status === "ReworkApproved" ||
              latestStep.action === "ReworkApproved" ||
              latestStep.status === "Approved" ||
              latestStep.action === "Approved" ||
              latestStep.status === "Partially Approved" ||
              latestStep.action === "Partially Approved"
            )) {
              currentStep = latestStep;
            }
          }
        }
        
        // Final fallback: find any step for this user level
        if (!currentStep) {
          currentStep = sub.workflowStepResponses.find(
            (step) => step.userLabel === userLabel
          );
        }
      }
      
      // CRITICAL: Check status fields FIRST because "Partially Approved" is computed on doc.getL2().status
      // but workflowStepResponse.status stays "Approved", so we MUST check status fields to catch it
      let status;
      if (userLabel === "L1") {
        status = sub?.newL1Status || sub?.L1?.status || sub?.l1?.status;
      } else if (userLabel === "L2") {
        status = sub?.newL2Status || sub?.L2?.status || sub?.l2?.status;
      } else if (userLabel === "Finance") {
        status = sub?.newL3Status || sub?.L3?.status || sub?.l3?.status || sub?.Finance?.status || sub?.finance?.status;
      } else {
        status = sub?.newL1Status || sub?.newL2Status || sub?.newL3Status || 
                 sub?.L1?.status || sub?.L2?.status || sub?.L3?.status ||
                 sub?.l1?.status || sub?.l2?.status || sub?.l3?.status;
      }
      
      // Check status fields first - this catches "Partially Approved" which is computed but not in workflowStepResponse.status
      if (status) {
        const statusLower = status.toLowerCase().trim();
        if (["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(statusLower)) {
          return true;
        }
        if (statusLower === "rejected" || statusLower === "stopped") {
          return true;
        }
      }

      // Fallback: Check current step's action/status (set when saveDecisionOnly=true)
      // Backend maps action to 'status' field; STOPPED = rejected (journey closed)
      const action = currentStep?.status || currentStep?.action;
      if (action && ["approved", "reworkapproved", "rework approved", "partially approved", "partiallyapproved"].includes(action.toLowerCase())) return true;
      if (action && (action.toLowerCase() === "rejected" || action.toLowerCase() === "stopped")) return true;

      return false;
    });

    setCanSendAll(allHaveDecisions);
  };

  // Helper function to track voucher status before sending
  const trackVoucherStatuses = (voucherData) => {
    const statusTracking = {
      timestamp: new Date().toISOString(),
      voucherId: vid,
      parentVoucher: {
        id: voucherData?.id,
        status: voucherData?.status,
        amount: voucherData?.amount,
      },
      subVouchers: [],
      summary: {
        total: 0,
        approved: 0,
        rejected: 0,
        pending: 0,
        inRework: 0,
      }
    };

    // Collect all sub-vouchers from different document lists
    const allSubVouchers = [
      ...(voucherData?.pendingDocs || []),
      ...(voucherData?.approvedDocs || []),
      ...(voucherData?.rejectedDocs || []),
      ...(voucherData?.pendingApprovedRejectedDocs || []),
    ];

    allSubVouchers.forEach((subVoucher) => {
      const subStatus = {
        id: subVoucher?.id,
        documentName: subVoucher?.documentName,
        amount: subVoucher?.dataModalAttributeSet?.find(
          (attr) => attr?.metadataTechnicalName === "amount"
        )?.attributeValue || 0,
        status: subVoucher?.status,
        l1Status: subVoucher?.l1?.status,
        l2Status: subVoucher?.l2?.status,
        l3Status: subVoucher?.l3?.status,
        approvalStatus: subVoucher?.dataModalAttributeSet?.find(
          (attr) => attr?.metadataTechnicalName === "approval_status"
        )?.attributeValue,
        workflowSteps: subVoucher?.workflowStepResponses?.map((step) => ({
          userLabel: step?.userLabel,
          status: step?.status,
          action: step?.action,
        })) || [],
        isInRework: checkIfVoucherInRework(subVoucher),
      };

      statusTracking.subVouchers.push(subStatus);
      statusTracking.summary.total++;

      // Categorize by status
      if (subStatus.isInRework) {
        statusTracking.summary.inRework++;
      } else if (subStatus.l1Status === "Approved" || subStatus.l2Status === "Approved" || subStatus.l3Status === "Approved") {
        statusTracking.summary.approved++;
      } else if (subStatus.l1Status === "Rejected" || subStatus.l2Status === "Rejected" || subStatus.l3Status === "Rejected") {
        statusTracking.summary.rejected++;
      } else {
        statusTracking.summary.pending++;
      }
    });

    return statusTracking;
  };

  // Handler for Send All to Next Level
  const handleSendAllToNextLevel = () => {
    if (!vid) {
      toast.error("Voucher ID not found");
      return;
    }

    const isL2OrFinance = userLabel === "L2" || userLabel === "Finance";

    // For L2/Finance: backend canSubmitVoucher is the single source of truth; skip local rework count
    if (isL2OrFinance) {
      voucherStatusApi({
        url: `${apiUrls.dmsService.canSubmitVoucher}/${vid}`,
        method: "get",
        onSuccess: (canSubmit) => {
          if (canSubmit) {
            proceedWithSendAll();
          } else {
            toast.error(
              "Cannot send all: one or more sub-vouchers are in rework or do not have a final decision. " +
              "Please wait for rework to be completed and resubmitted before sending."
            );
            updateVoucherData();
          }
        },
        onError: () => {
          toast.error("Unable to verify if voucher can be submitted. Please try again.");
          updateVoucherData();
        },
      });
      return;
    }

    // Pre-check using local data for optimistic UI feedback (backend canSubmitVoucher API is the final source of truth)
    const subVouchers = Array.isArray(outputData) ? outputData : [];
    const actualSubVouchers = subVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
    const vouchersInRework = actualSubVouchers.filter((sub) => checkIfVoucherInRework(sub));
    if (vouchersInRework.length > 0) {
      toast.error(
        `Cannot send all: ${vouchersInRework.length} sub-voucher(s) are in rework state. ` +
        "Please wait for rework to be completed and resubmitted before sending."
      );
      updateVoucherData();
      return;
    }

    proceedWithSendAll();

    function proceedWithSendAll() {
      // Step 1: First fetch latest voucher details to track status before sending
      voucherStatusApi({
        url: apiUrls.dmsService.getVoucherDetails + vid,
        method: "post",
        onSuccess: (voucherData) => {
          // Track status of each voucher before sending
          trackVoucherStatuses(voucherData);

          // For L2/Finance, backend canSubmitVoucher is source of truth - skip local rework check
          if (!isL2OrFinance) {
            const allDocs = [
              ...(voucherData?.pendingDocs || []),
              ...(voucherData?.approvedDocs || []),
              ...(voucherData?.rejectedDocs || []),
              ...(voucherData?.pendingApprovedRejectedDocs || []),
            ];
            const stillInRework = allDocs.filter((doc) => checkIfVoucherInRework(doc));
            if (stillInRework.length > 0) {
              toast.error(
                `Cannot send all: ${stillInRework.length} sub-voucher(s) are in rework state. ` +
                "Status has changed. Please refresh and try again."
              );
              const nextType = state?.type || voucherData?.type || "Rework";
              setDataState({ ...voucherData, type: nextType });
              return;
            }
          }

          // Step 2: Proceed with sending all to next level
          voucherStatusApi({
            url: `${apiUrls.dmsService.sendAllToNextLevel}/${vid}`,
            method: "post",
            dataToSend: {},
            onSuccess: () => {
              toast.success(
                userLabel === "Finance"
                  ? "All sub-vouchers Submitted successfully"
                  : "All sub-vouchers sent to next level successfully"
              );
              setCanSendAll(false);
              updateVoucherData(() => {
                setDecisionStatusMap({});
              });
            },
            onError: (error) => {
              const errorMessage = error?.response?.data?.message || "Failed to send all to next level";
              toast.error(errorMessage);
              if (errorMessage.includes("rework") || errorMessage.includes("Rework")) {
                toast.warning(
                  "Some vouchers are in rework state. Please wait for rework to be completed before sending."
                );
                updateVoucherData();
              } else if (errorMessage.includes("Please complete all sub-voucher actions")) {
                toast.warning("Please complete all sub-voucher decisions before proceeding");
                updateVoucherData();
              }
            },
          });
      },
      onError: (error) => {
        const errorMessage = error?.response?.data?.message || "Failed to fetch voucher details";
        toast.error(`Cannot proceed: ${errorMessage}`);
        console.error("Error fetching voucher details before send all:", error);
      },
    });
  };

  };

  // Fetch details for non-Store users when needed. Store users use navigation state from getAllRecords when available.
  useEffect(() => {
    if (!vid) return;

    // Store users: fetch when state is missing (reload, direct URL, or after rework submit left state empty).
    // Prefer `location.state` over React `state` so we do not skip fetch when `vid` changes but useState still
    // holds the previous voucher until the sync effect runs.
    if (userLabel === "Store") {
      const source = fromPrevScreen ?? state;
      const reworkEntry = source?.type === "Rework";
      if (
        !source ||
        !storeVoucherStateHasRenderableDocs(source, {
          reworkListNavigation: reworkEntry,
        })
      ) {
        updateVoucherData();
      }
      return;
    }

    const hasDocList = (arr) => Array.isArray(arr) && arr.length > 0;
    const hasRequiredDocs =
      hasDocList(state?.pendingDocs) ||
      hasDocList(state?.approvedDocs) ||
      hasDocList(state?.rejectedDocs) ||
      hasDocList(state?.pendingApprovedRejectedDocs);

    // Do not key off type === "Rework" | "Approved" | "Rejected" alone: after updateVoucherData(),
    // navigate() replaces location.state with a new object reference, which re-runs this effect.
    // If shouldFetch stayed true for those types, we would refetch forever and hammer canSubmitVoucher.
    const shouldFetch = !state || !state?.type || !hasRequiredDocs;

    if (shouldFetch) {
      updateVoucherData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid, state?.type, userLabel, fromPrevScreen]);

  // Content fingerprint so we do not re-call canSubmitVoucher on every new state object reference
  // with identical voucher rows (e.g. duplicate setDataState + navigate state).
  const canSubmitRefreshKey = useMemo(() => {
    if (!state || typeof state !== "object") return "";
    const rowSig = (arr) =>
      Array.isArray(arr) && arr.length
        ? arr
            .map(
              (d) =>
                `${d?.id ?? ""}:${d?.status ?? ""}:${d?.workflowState ?? ""}`
            )
            .sort()
            .join(";")
        : "";
    return [
      String(vid ?? ""),
      state.type,
      state.status,
      rowSig(state.pendingDocs),
      rowSig(state.approvedDocs),
      rowSig(state.rejectedDocs),
      rowSig(state.pendingApprovedRejectedDocs),
    ].join("|");
  }, [vid, state]);

  // Update decision status when outputData changes
  // For ALL levels (L1, L2, Finance): drive Submit All button from backend canSubmitVoucher API (single source of truth)
  // This ensures consistent logic across all approval levels and prevents frontend/backend mismatches
  useEffect(() => {
    if (!vid || !state || (state?.type !== "Pending" && state?.type !== "Rework")) {
      return;
    }
    
    // Update decision status map from API data (used for optimistic UI updates)
    if (state && outputData) {
      updateDecisionStatusFromData(state);
    }
    
    voucherStatusApi({
      url: `${apiUrls.dmsService.canSubmitVoucher}/${vid}`,
      method: "get",
      onSuccess: (canSubmit) => {
        setCanSendAll(Boolean(canSubmit));
      },
      onError: () => {
        setCanSendAll(false);
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vid, userLabel, state?.type, canSubmitRefreshKey]);

  const approveRequestHandler = (ids = [], comment = "", saveDecisionOnly = true) => {
    // CRITICAL: Always send saveDecisionOnly: true for Approve/Partial Approve - only Submit Voucher should advance workflow
    const saveOnly = true;
    let payload = {
      approved: ids?.map((i) => ({
        id: i.id,
        approvedAmount: i.approvedAmount,
      })),
      comment,
      rejected: [],
      saveDecisionOnly: saveOnly,
    };
    voucherStatusApiCall(payload, "Decision saved successfully", saveOnly);
  };

  const updateCommentRequestHandler = (params) => {
    let payload = {
      id: params?.id,
      voucherComment: params?.comment,
    };
    updateCommentApiCall(payload, "Voucher comment successfully updated.");
  };

  const rejectRequestHandler = (ids, comment = "", saveDecisionOnly = true) => {
    // CRITICAL: Always send saveDecisionOnly: true for Reject - only Submit Voucher should advance workflow
    const saveOnly = true;
    let payload = {
      approved: [],
      comment: comment,
      rejected: ids,
      saveDecisionOnly: saveOnly,
    };
    voucherStatusApiCall(payload, "Decision saved successfully", saveOnly);
  };

  const sendBackForReworkHandler = (ids = [], comment = "") => {
    let payload = {
      approved: [],
      rejected: [],
      reworkRequested: Array.isArray(ids) ? ids : [ids],
      comment: comment,
    };
    voucherStatusApi({
      url: apiUrls.dmsService.sendBackForRework,
      dataToSend: payload,
      method: "post",
      onSuccess: () => {
        setIsRejectVisible({ isOpen: false });
        setIsEditApproveVisible({ isOpen: false });
        setIsSendBackForReworkVisible({ isOpen: false });
        toast.success("Sent Back for Rework Successfully");
        updateVoucherData();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to send back for rework");
      },
    });
  };

  const showStatusColor = (key) => {
    switch (key) {
      case "Pending":
        return "text-[var(--pendingYellow)]";
      case "Approved":
        return "text-[var(--successGreen)]";
      case "Rejected":
        return "text-[var(--textRed)]";
      case "Partially Approved":
        return "text-[var(--pendingYellow)]";
      case "NA":
        return "text-[var(--blackColor)]";
      default:
        return "text-[var(--blackColor)]";
    }
  };

  //outputData.map(d => d.L1)[0]?.l1_amount_approved
  const Component = ({ data }) => {
    return (
      <div>
        <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
          <div className="min-w-[24rem] font-size-small">Status:</div>
          <div className={`font-size-small ${showStatusColor(data?.status)}`}>
            {data?.status}
          </div>
        </div>
        <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
          <div className="min-w-[24rem] font-size-small">
            Request Amount (₹):
          </div>
          <div className="font-size-small">
            {data?.status === "Rejected" ? 0 : data?.amount || 0}
          </div>
        </div>
        <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
          <div className="min-w-[24rem] font-size-small">
            Approved Amount (₹):
          </div>
          <div className="font-size-small">
            {data?.status === "Rejected" ? 0 : data.approvedAmount || 0}
          </div>
        </div>
        <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
          <div className="min-w-[24rem] font-size-small">Action Date:</div>
          <div className="font-size-small">{data?.actionDate || ""}</div>
        </div>
        <div className="h-[4rem] border-b-[1px] border-[var(--borderGray2)] flex items-center px-5">
          <div className="min-w-[24rem] font-size-small">Action Comment:</div>
          <div className="font-size-small">
            {sanitizeActionComment(data?.actionComment)}
          </div>
        </div>
        <div className="h-[4rem] flex items-center px-5">
          <div className="min-w-[24rem] font-size-small">Action By:</div>
          <div className="font-size-small">{data?.actionBy}</div>
        </div>
      </div>
    );
  };
  // Parent L1/L2/L3 live on the voucher payload (`state` from getVoucherDetails). For non-Store, `outputData` is
  // line items only, so parent workflow objects must not be read from it.
  const parentVoucherForAccordion = userLabel === "Store" ? outputData : state;
  const parentL1 = parentVoucherForAccordion?.L1 ?? parentVoucherForAccordion?.l1;
  const parentL2 = parentVoucherForAccordion?.L2 ?? parentVoucherForAccordion?.l2;
  const parentL3 =
    parentVoucherForAccordion?.L3 ??
    parentVoucherForAccordion?.l3 ??
    parentVoucherForAccordion?.Finance ??
    parentVoucherForAccordion?.finance;
  const parentVoucherAmount =
    parentVoucherForAccordion?.amount ?? state?.amount;

  const accordionItems = [
    {
      isArrowEnable: parentL1 && parentL1?.status !== "Pending",
      renderLeft: () => {
        return (
          <div className={`font-family-medium font-size-small`}>
            L1 - Area Manager
          </div>
        );
      },
      renderRight: () => {
        if (parentL1?.status === "Approved") {
          return (
            <div className="font-family-medium font-size-small text-[var(--successGreen)]">
              Approved | ₹ {parentL1?.approvedAmount}
            </div>
          );
        }

        if (parentL1?.status === "Partially Approved")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL1?.status} | ₹ {parentL1?.approvedAmount}
            </div>
          );

        if (parentL1?.status === "Rejected")
          return (
            <div className="font-family-medium font-size-small text-[var(--textRed)]">
              {parentL1?.status}
            </div>
          );

        if (parentL1?.status === "Pending")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL1?.status}
            </div>
          );
        else
          return <div className="font-family-medium font-size-small">NA</div>;
      },
      Component: () => {
        return (
          <Component data={{ ...parentL1, amount: parentVoucherAmount }} />
        );
      },
    },
    {
      // Only enable L2 dropdown when L2 has actually taken action (not just "Partially Approved" with ₹0 from aggregation)
      isArrowEnable: parentL2 && parentL2?.status !== "Pending" && (
        (Number(parentL2?.approvedAmount) > 0) ||
        ["Rejected", "Rework Requested", "Rework Pending", "Approved", "Rework Approved"].includes(parentL2?.status)
      ),
      renderLeft: () => {
        return (
          <div className="font-family-medium font-size-small">
            L2 - Regional Manager
          </div>
        );
      },
      renderRight: () => {
        if (parentL2?.status === "Approved") {
          return (
            <div className="font-family-medium font-size-small text-[var(--successGreen)]">
              Approved | ₹ {parentL2?.approvedAmount}
            </div>
          );
        }

        if (parentL2?.status === "Rejected")
          return (
            <div className="font-family-medium font-size-small text-[var(--textRed)]">
              {parentL2?.status}
            </div>
          );

        if (parentL2?.status === "Partially Approved")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL2?.status} | ₹ {parentL2?.approvedAmount}
            </div>
          );

        if (parentL2?.status === "Pending")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL2?.status}
            </div>
          );
        else
          return <div className="font-family-medium font-size-small">NA</div>;
      },
      Component: () => {
        // Request Amount for L2 = amount received from L1. Ensure it's never less than L2 approved (avoids inconsistent "Approved > Request" when L1 total is under-reported e.g. after rework).
        const l1Approved = Number(parentL1?.approvedAmount) || 0;
        const l2Approved = Number(parentL2?.approvedAmount) || 0;
        const requestAmount = Math.max(l1Approved, l2Approved);
        return (
          <Component
            data={{ ...parentL2, amount: requestAmount }}
          />
        );
      },
    },
    {
      // Only enable L3 dropdown when L3 has actually taken action (not just "Partially Approved" with ₹0 from aggregation)
      isArrowEnable: parentL3 && parentL3?.status !== "Pending" && (
        (Number(parentL3?.approvedAmount) > 0) ||
        ["Rejected", "Rework Requested", "Rework Pending", "Approved", "Rework Approved"].includes(parentL3?.status)
      ),
      renderLeft: () => {
        return (
          <div className="font-family-medium font-size-small">
            L3 - Accounts Manager
          </div>
        );
      },
      renderRight: () => {
        if (parentL3?.status === "Approved") {
          return (
            <div className="font-family-medium font-size-small text-[var(--successGreen)]">
              Approved | ₹ {parentL3?.approvedAmount}
            </div>
          );
        }

        if (parentL3?.status === "Rejected")
          return (
            <div className="font-family-medium font-size-small text-[var(--textRed)]">
              {parentL3?.status}
            </div>
          );

        if (parentL3?.status === "Partially Approved")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL3?.status} | ₹ {parentL3?.approvedAmount}
            </div>
          );

        if (parentL3?.status === "Pending")
          return (
            <div className="font-family-medium font-size-small text-[var(--pendingYellow)]">
              {parentL3?.status}
            </div>
          );
        else
          return <div className="font-family-medium font-size-small">NA</div>;
      },
      Component: () => {
        // Request Amount for L3 = amount received from L2. Ensure it's never less than L3 approved (same consistency as L2).
        const l2Approved = Number(parentL2?.approvedAmount) || 0;
        const l3Approved = Number(parentL3?.approvedAmount) || 0;
        const requestAmount = Math.max(l2Approved, l3Approved);
        return (
          <Component
            data={{ ...parentL3, amount: requestAmount }}
          />
        );
      },
    },
  ];

  const isL1 = userLabel !== "Store";

  const getFile = (file_id, onSuccess) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess,
    });
  };

  const onClickDownload = () => {
    let reportSettings = {
      fileName: "All Vouchers", // Name of the resulting spreadsheet
      extraLength: 3, // A bigger number means that columns will be wider
      writeMode: "writeFile", // The available parameters are 'WriteFile' and 'write'. This setting is optional. Useful in such cases https://docs.sheetjs.com/docs/solutions/output#example-remote-file
      writeOptions: {}, // Style options from https://docs.sheetjs.com/docs/api/write-options
      RTL: false, // Display the columns from right-to-left (the default value is false)
    };

    downloadReports(
      [
        {
          sheet: "All Vouchers",
          columns: [
            {
              label: "V.ID",
              value: ({ original: row }) => row.id,
            }, // Top level data
            {
              label: "E.ID",
              value: ({ original: row }) => row.childId || "-",
            }, // Top level data
            { label: "Submitted On", value: ({ original: row }) => row.date }, // Top level data
            { label: "Amount", value: ({ original: row }) => row.amount }, // Top level data
            {
              label: "L1 Status",
              value: ({ original: row }) => row.L1?.status || "-",
            }, // Top level data
            {
              label: "L2 Status",
              value: ({ original: row }) => row.L2?.status || "-",
            }, // Top level data
            {
              label: "A/C Status",
              value: ({ original: row }) => row.L3?.status || "-",
            }, // Top level data
            {
              label: "Approved Amount",
              value: ({ original: row }) => row?.L3?.approvedAmount || "-",
            }, // Top level data
          ],
          content: selectedCheckBoxes || [],
        },
      ],
      reportSettings
    );
  };

  const onClickViewFile = (file_id, filename) => {
    fileDownload({
      url: apiUrls.dmsService.getFile + "/" + file_id,
      method: "get",
      responseType: "blob",
      onSuccess: (apiData) => {
        setDownloadPreviewOpenWithData({
          file: apiData.data,
          filename,
          isOpen: true,
        });
      },
    });
  };

  const handleEditAfterRework = (rowData) => {
    // For store users, use the parent voucher data (state) instead of individual row data
    // The voucher ID comes from vid param, and we need the full voucher object
    const voucherDataForEdit = userLabel === "Store" 
      ? { ...state, id: vid, vid: vid, head: rowData?.head, comment: rowData?.comment }
      : { ...rowData, id: rowData?.id || vid, vid: vid };
    
    setIsEditAfterReworkVisible({
      isOpen: true,
      data: voucherDataForEdit,
    });
  };

  const editAfterReworkHandler = (payload, onComplete) => {
    voucherStatusApi({
      url: apiUrls.dmsService.editVoucherAfterRework,
      dataToSend: payload,
      method: "post",
      onSuccess: (apiData) => {
        toast.success("Voucher updated successfully");
        setIsEditAfterReworkVisible({ isOpen: false, data: {} });
        // Store: always refetch voucher details so all line items (including remaining rework items)
        // are shown without having to go back and re-open. Non-Store can use response if shape is correct.
        if (userLabel === "Store") {
          updateVoucherData(() => {
            if (onComplete) onComplete();
          }, true);
          return;
        }
        const hasVoucherDetailShape = apiData && (
          Array.isArray(apiData.pendingDocs) ||
          Array.isArray(apiData.approvedDocs) ||
          Array.isArray(apiData.reworkRequestedDocs)
        );
        if (hasVoucherDetailShape) {
          const nextType = state?.type || apiData?.type || "Rework";
          const newState = { ...apiData, type: nextType };
          setDataState(newState);
          try {
            sessionStorage.setItem(`voucher_${vid}_state`, JSON.stringify(newState));
          } catch (e) {
            console.error("Error saving to sessionStorage:", e);
          }
          if (onComplete) onComplete();
        } else {
          updateVoucherData(() => {
            if (onComplete) onComplete();
          }, true);
        }
      },
      onError: (error) => {
        toast.error(error?.response?.data?.message || "Failed to update voucher");
        if (onComplete) onComplete();
      },
    });
  };

  const memoStoreColumns = useMemo(
    () => adminColumns({
      onClickDownloadFile,
      onClickViewFile,
      onEditClick: handleEditAfterRework,
      userLabel,
      voucherData: state,
    }),
    [userLabel, state, outputData] // Include outputData to recalculate when voucher status changes
  );

  const isL2 = userLabel === "L2";
  const isFinance = userLabel === "Finance";

  const inBetweenColumnsData = isL2
    ? [{ Header: "Approved by L1", accessor: "l1_amount_approved" }]
    : [
        { Header: "Approved by L1", accessor: "l1_amount_approved" },
        { Header: "Approved by L2", accessor: "l2_amount_approved" },
      ];

  const handleOpenReworkModal = (modalState) => {
    if (!isSendBackForReworkVisible.isOpen) {
      setIsSendBackForReworkVisible(modalState);
    }
  };

  const nonStoreColumns = useMemo(
    () =>
      areaManagerColumns({
        setIsModalVisible,
        approveRequestHandler,
        rejectRequestHandler,
        getFile,
        setIsEditApproveVisible,
        setIsRejectVisible,
        setIsSendBackForReworkVisible: handleOpenReworkModal,
        onClickDownloadFile,
        onClickViewFile,
        setIsEditVoucherCommentVisible,
        type: state?.type,
        inBetweenColumns:
          (isL2 || isFinance) && state?.type === "Pending"
            ? inBetweenColumnsData
            : [],
        vid: vid,
        userLabel,
        decisionStatusMap, // Pass decision status map to columns
      }),
    [userLabel, decisionStatusMap]
  );

  let voucherIdDetails = () => {
    let details = "";
    if (isL1) {
      let storeList = storeDropdownState;
      if (storeList?.length === 0) {
        try {
          storeList = JSON.parse(localStorage.getItem("storeList"));
        } catch {
          storeList = [];
        }
      }
      if (storeList?.length > 0) {
        let getSelectedStoreName = storeList?.filter(
          (item) => item?.storeCode === outputData[0]?.store_code
        );
        if (getSelectedStoreName?.length > 0) {
          details = getSelectedStoreName[0]?.name;
        }
      }
      if (details) {
        details += " - ";
      }
      details += (outputData[0]?.store_code ?? "");
    }
    const trimmed = details?.trim?.() ?? "";
    if (trimmed) {
      return `(${trimmed})`;
    }
    return null;
  };

  return (
    <div className="dashboard-container">
      <ToolbarHeader
        showBack
        renderLeft={() => {
          return (
            <div className="flex items-center text-[3rem] font-family-medium ">
              <div className="text-[var(--blackColor)]">
                Voucher ID: {vid} {voucherIdDetails()}
              </div>
              {!isL1 && outputData?.status && (
                <>
                  <span className="mx-6">|</span>
                  <div className="text-[var(--pendingYellow)]">
                    {outputData?.status}
                  </div>
                </>
              )}
            </div>
          );
        }}
        containerClassName="mt-14 mb-10"
        renderRight={() => {
          return (
            <div className="text-[3rem] font-family-medium text-[var(--blackColor)]">
              Amount: ₹{" "}
              {state?.type === "Approved"
                ? (userLabel === "L1"
                    ? (calculateTotalApprovedAmount !== null
                        ? calculateTotalApprovedAmount
                        : state?.l1AmountApproved != null && state?.l1AmountApproved !== ""
                          ? Number(state.l1AmountApproved)
                          : (state?.approvedAmount ?? 0))
                    : ((state?.approvedAmount || state?.l1AmountApproved || state?.l2AmountApproved || state?.l3AmountApproved || calculateTotalApprovedAmount) ?? 0))
                : state?.type === "Pending"
                ? (calculateTotalApprovedAmount !== null ? calculateTotalApprovedAmount : state?.pendingAmount)
                : state?.type === "Rejected"
                ? (voucherRejectedAmountForViewer(state, userLabel) ?? state?.rejectedAmount ?? 0)
                : state?.type === "All"
                ? userLabel === "L1"
                  ? state?.pendingAmount === 0
                    ? (calculateTotalApprovedAmount !== null
                        ? calculateTotalApprovedAmount
                        : state?.l1AmountApproved != null && state?.l1AmountApproved !== ""
                          ? Number(state.l1AmountApproved)
                          : state?.approvedAmount)
                    : (calculateTotalApprovedAmount !== null ? calculateTotalApprovedAmount : state?.pendingAmount)
                  : state?.pendingAmount === 0
                    ? state?.approvedAmount
                    : (calculateTotalApprovedAmount !== null ? calculateTotalApprovedAmount : state?.pendingAmount)
                : (calculateTotalApprovedAmount !== null ? calculateTotalApprovedAmount : outputData?.amount)}
            </div>
          );
        }}
      />

      {!isL1 && (
        <CustomAccordion
          items={accordionItems}
          activeIndex={activeAccordion}
          setActiveIndex={setActiveAccordion}
        />
      )}

      <div className="mt-4 p-4 bg-[var(--whiteColor)] rounded-[1rem] max-table-width">
        <CustomTable
          columns={isL1 ? nonStoreColumns : memoStoreColumns}
          data={isL1 ? outputData : storeTableData}
          centerAlignedColumns={[
            "l1_status",
            "l2_status",
            "amount",
            "ac_status",
            "approved_amount",
          ]}
          showCheckboxes={
            isL1 &&
            state?.type !== "All" &&
            state?.type !== "Approved" &&
            state?.type !== "Rejected" &&
            state?.type !== "Rework"
          }
          onCheckboxSelect={(checkboxes) => {
            if (outputData?.length && isL1) {
              setSelectedCheckBoxes(checkboxes);
            }
          }}
        />
      </div>

      {userLabel !== "Store" &&
        state?.type !== "All" &&
        state?.type !== "Approved" &&
        state?.type !== "Rejected" &&
        state?.type !== "Rework" &&
        // Show buttons for Pending (not for Rework History - read-only journey view there)
        ((state?.type === "Pending") ||
         (state?.pendingDocs?.length > 0 || state?.pendingApprovedRejectedDocs?.length > 0)) && (
          <div className={`mt-4 flex items-center justify-end gap-x-5`}>
            {(() => {
              const actionsDisabledByRework = hasAnyChildInRework(state, userLabel);
              return (
                <>
            <CustomButton
              title="Submit Voucher"
              wrapperClassName={`border-[1px] !border-[var(--successGreen)] !bg-transparent self-end !text-[var(--successGreen)] !px-8 ${
                // CRITICAL: Backend canSubmitVoucher API already checks rework state, so we don't need actionsDisabledByRework check here
                // The API returns false if any voucher is in rework, so if canSendAll is true, vouchers are NOT in rework
                canSendAll ? "" : "disabled-kv opacity-50 cursor-not-allowed"
              }`}
              onClick={() => {
                // CRITICAL: Backend canSubmitVoucher API already checks rework state, so we don't need actionsDisabledByRework check here
                if (canSendAll) {
                  handleSendAllToNextLevel();
                } else {
                  // Check if vouchers are in rework to provide specific message
                  const subVouchers = Array.isArray(outputData) ? outputData : [];
                  const actualSubVouchers = subVouchers.filter((sub) => !sub.subRows || sub.subRows.length === 0);
                  const vouchersInRework = actualSubVouchers.filter((sub) => checkIfVoucherInRework(sub));
                  
                  if (vouchersInRework.length > 0) {
                    toast.warning(
                      `Cannot submit: ${vouchersInRework.length} sub-voucher(s) are in rework state. ` +
                      "Please wait for rework to be completed before submitting."
                    );
                  } else {
                    toast.info("Please decide all sub-vouchers (approve/reject) before submitting.");
                  }
                }
              }}
            />
            {/* <CustomButton
            title="Download"
            wrapperClassName={`border-[1px] !border-[var(--blackColor)] !bg-transparent self-end !text-[var(--blackColor)] !px-8 ${
              selectedCheckBoxes?.length > 0 ? "" : "disabled-kv"
            }`}
            onClick={onClickDownload}
          /> */}

            <CustomButton
              title="Reject"
              wrapperClassName={`border-[1px] !border-[var(--blackColor)] !bg-transparent self-end !text-[var(--blackColor)] !px-8 ${
                (canRejectSelectedVouchers() && !actionsDisabledByRework) ? "" : "disabled-kv opacity-50 cursor-not-allowed"
              }`}
              onClick={() => {
                if (actionsDisabledByRework) {
                  toast.warning(
                    "One or more sub-vouchers are in rework. Please wait for rework to be completed first."
                  );
                  return;
                }
                if (!canRejectSelectedVouchers()) {
                  // Check why button is disabled and show appropriate message
                  const vouchersToCheck = getVouchersToProcess();
                  
                  if (vouchersToCheck.length === 0) {
                    toast.info(userLabel === "L1" ? "Please select vouchers to reject." : "No vouchers available to reject.");
                    return;
                  }
                  
                  const alreadyRejected = vouchersToCheck.filter((v) => isVoucherRejected(v));
                  const alreadyApproved = vouchersToCheck.filter((v) => isVoucherApproved(v));
                  const inRework = vouchersToCheck.filter((v) => checkIfVoucherInRework(v));
                  
                  if (alreadyRejected.length > 0) {
                    toast.warning(
                      `Cannot reject: ${alreadyRejected.length} voucher(s) are already rejected.`
                    );
                  } else if (alreadyApproved.length > 0) {
                    toast.warning(
                      `Cannot reject: ${alreadyApproved.length} voucher(s) are already approved. ` +
                      "Please send back for rework first if you need to change the decision."
                    );
                  } else if (inRework.length > 0) {
                    toast.warning(
                      `Cannot reject: ${inRework.length} voucher(s) are in rework state. ` +
                      "Please wait for rework to be completed first."
                    );
                  } else {
                    toast.info("Please select vouchers that are pending at your level.");
                  }
                  return;
                }
                
                const ids = [];
                if (userLabel === "L1") {
                  // L1: use selectedCheckBoxes
                  selectedCheckBoxes.forEach((row) => {
                    if (row?.original?.subRows) {
                      row?.original?.subRows?.forEach((row) => {
                        if (row?.id) {
                          ids.push(row?.id);
                          return;
                        }

                        row?.subRows?.forEach((row) => {
                          if (row?.id) {
                            ids.push(row?.id);
                            return;
                          }

                          row?.subRows?.forEach((row) => {
                            if (row?.id) {
                              ids.push(row?.id);
                              return;
                            }
                          });
                        });
                      });
                    } else {
                      ids.push(row?.original?.id);
                    }
                  });
                } else {
                  // L2/Finance: use vouchers from outputData that can be rejected
                  const vouchersToProcess = getVouchersToProcess();
                  vouchersToProcess.forEach((voucher) => {
                    if (voucher?.id && 
                        !isVoucherRejected(voucher) && 
                        !isVoucherApproved(voucher) && 
                        !checkIfVoucherInRework(voucher)) {
                      ids.push(voucher.id);
                    }
                  });
                }

                const validIds = [...new Set([...ids])];

                // rejectRequestHandler(validIds);
                setIsRejectVisible({ data: validIds, isOpen: true });
              }}
            />
            <CustomButton
              title="Approve"
              wrapperClassName={`border-[1px] !border-[var(--blackColor)] !bg-transparent self-end !text-[var(--blackColor)] !px-8 ${
                (canApproveSelectedVouchers() && !actionsDisabledByRework) ? "" : "disabled-kv opacity-50 cursor-not-allowed"
              }`}
              onClick={() => {
                if (actionsDisabledByRework) {
                  toast.warning(
                    "One or more sub-vouchers are in rework. Please wait for rework to be completed first."
                  );
                  return;
                }
                if (!canApproveSelectedVouchers()) {
                  // Check why button is disabled and show appropriate message
                  const vouchersToCheck = getVouchersToProcess();
                  
                  if (vouchersToCheck.length === 0) {
                    toast.info(userLabel === "L1" ? "Please select vouchers to approve." : "No vouchers available to approve.");
                    return;
                  }
                  
                  const alreadyApproved = vouchersToCheck.filter((v) => isVoucherApproved(v));
                  const alreadyRejected = vouchersToCheck.filter((v) => isVoucherRejected(v));
                  const inRework = vouchersToCheck.filter((v) => checkIfVoucherInRework(v));
                  
                  if (alreadyApproved.length > 0) {
                    toast.warning(
                      `Cannot approve: ${alreadyApproved.length} voucher(s) are already approved.`
                    );
                  } else if (alreadyRejected.length > 0) {
                    toast.warning(
                      `Cannot approve: ${alreadyRejected.length} voucher(s) are already rejected. ` +
                      "Vouchers must be resubmitted before they can be approved."
                    );
                  } else if (inRework.length > 0) {
                    toast.warning(
                      `Cannot approve: ${inRework.length} voucher(s) are in rework state. ` +
                      "Please wait for rework to be completed and resubmitted first."
                    );
                  } else {
                    toast.info("Please select vouchers that are pending at your level.");
                  }
                  return;
                }
                
                const ids = [];
                if (userLabel === "L1") {
                  // L1: use selectedCheckBoxes
                  selectedCheckBoxes.forEach((row) => {
                    if (row?.original?.subRows) {
                      row?.original?.subRows?.forEach((row) => {
                        if (row?.id) {
                          ids.push({ id: row?.id, approvedAmount: row?.amount });
                          return;
                        }

                        row?.subRows?.forEach((row) => {
                          if (row?.id) {
                            ids.push({
                              id: row?.id,
                              approvedAmount: row?.amount,
                            });
                            return;
                          }

                          row?.subRows?.forEach((row) => {
                            if (row?.id) {
                              ids.push({
                                id: row?.id,
                                approvedAmount: row?.amount,
                              });
                              return;
                            }
                          });
                        });
                      });
                    } else {
                      ids.push({
                        id: row?.original?.id,
                        approvedAmount: approvedAmountPassAccordingToLevel(
                          row?.original
                        ),
                      });
                    }
                  });
                } else {
                  // L2/Finance: use vouchers from outputData that can be approved
                  const vouchersToProcess = getVouchersToProcess();
                  vouchersToProcess.forEach((voucher) => {
                    if (voucher?.id && 
                        !isVoucherApproved(voucher) && 
                        !isVoucherRejected(voucher) && 
                        !checkIfVoucherInRework(voucher)) {
                      ids.push({
                        id: voucher.id,
                        approvedAmount: approvedAmountPassAccordingToLevel(voucher),
                      });
                    }
                  });
                }

                const validIds = [...new Set([...ids])];
                approveRequestHandler(validIds, "", true); // saveDecisionOnly = true
              }}
            />
                </>
              );
            })()}
            
          </div>
        )}

      <PreviewModal
        isModalVisible={isModalVisible}
        setIsModalVisible={setIsModalVisible}
      />
      <PreviewDocModal
        isModalVisible={downloadPreviewOpenWithData}
        setIsModalVisible={setDownloadPreviewOpenWithData}
      />

      {isRejectVisible.isOpen && (
        <RejectModal
          isModalVisible={isRejectVisible}
          setIsModalVisible={setIsRejectVisible}
          rejectHandler={({ data = [], comment = "" }) => {
            const validIds = [...new Set([...data])];
            rejectRequestHandler(validIds, comment, true); // saveDecisionOnly = true
          }}
        />
      )}
      {isEditApproveVisible.isOpen && (
        <EditApproveModal
          isModalVisible={isEditApproveVisible}
          setIsModalVisible={setIsEditApproveVisible}
          approveHandler={({ data = [], comment = "" }) => {
            const validIds = [...new Set([...data])];
            approveRequestHandler(validIds, comment, true); // saveDecisionOnly = true
          }}
          ids={isEditApproveVisible?.previousAmounts}
          totalAmount={state?.amount || "NA"}
        />
      )}
      {isEditVoucherCommentVisible.isOpen && (
        <EditVoucherComment
          isModalVisible={isEditVoucherCommentVisible}
          setIsModalVisible={setIsEditVoucherCommentVisible}
          approveHandler={updateCommentRequestHandler}
          ids={isEditApproveVisible?.previousAmounts}
          totalAmount={state?.amount || "NA"}
        />
      )}
      {isSendBackForReworkVisible.isOpen && (
        <SendBackForReworkModal
          isModalVisible={isSendBackForReworkVisible}
          setIsModalVisible={setIsSendBackForReworkVisible}
          reworkHandler={({ data, comment = "" }) => {
            sendBackForReworkHandler(data, comment);
          }}
        />
      )}
      {isEditAfterReworkVisible.isOpen && (
        <EditAfterReworkModal
          isModalVisible={isEditAfterReworkVisible}
          setIsModalVisible={setIsEditAfterReworkVisible}
          approveHandler={editAfterReworkHandler}
          voucherData={isEditAfterReworkVisible.data}
        />
      )}
    </div>
  );
};

export default VoucherDetails;
